import type { JSX, ReactNode } from 'react';
import {
  SESSION_END_REASON_LABELS,
  SESSION_STATE_HINTS,
  type SessionSummary,
} from '@teams-avatar/shared';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui';
import { formatDuration, formatTimestamp } from '../lib/format';

interface Props {
  sessions: SessionSummary[];
  emptyMessage: string;
  /** Historie zeigt Endzeit und Grund statt Laufzeit-Live-Wert. */
  variant: 'active' | 'history';
  onStop?: (id: string) => void;
  onScreenshot?: (id: string) => void;
  onToggleLog?: (id: string) => void;
  /** Schickt die Taste "m" an die AVA-Seite. */
  onMic?: (id: string) => void;
  /** Session, an die gerade gesendet wurde — fuer die kurze Rueckmeldung. */
  micSentId?: string | null;
  /** Erneut beitreten. Nur fuer Sessions in rejoinableIds angeboten. */
  onRejoin?: (id: string) => void;
  rejoinableIds?: ReadonlySet<string>;
  busyId?: string | null;
  openLogId?: string | null;
}

/**
 * Zwei Darstellungen derselben Daten: Karten auf schmalen Bildschirmen, Tabelle
 * ab Tablet-Breite.
 *
 * Eine seitlich scrollende Tabelle waere auf dem Handy unbenutzbar, und die
 * Oberflaeche wird ausdruecklich auch von unterwegs benutzt. Die Reihenfolge der
 * Angaben ist in beiden Faellen dieselbe, damit man sich nicht umgewoehnen muss.
 */
export function SessionTable({
  sessions,
  emptyMessage,
  variant,
  onStop,
  onScreenshot,
  onToggleLog,
  onMic,
  micSentId = null,
  onRejoin,
  rejoinableIds,
  busyId = null,
  openLogId = null,
}: Props): JSX.Element {
  if (sessions.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border-strong bg-bg p-10 text-center text-sm text-fg-muted">
        {emptyMessage}
      </div>
    );
  }

  /**
   * Groesse je Darstellung: in der Karte auf dem Handy sind es Ziele fuer den
   * Daumen, in der Tabelle am Rechner Ziele fuer den Mauszeiger. Dieselben
   * Knoepfe in derselben Reihenfolge, nur groesser.
   */
  const actions = (session: SessionSummary, size: 'sm' | 'md'): ReactNode =>
    variant !== 'active' ? (
      /*
       * In der Historie nur der erneute Beitritt, und den nur fuer die
       * juengsten Sessions — ein alter Meeting-Link fuehrt zu einem Beitritt,
       * der scheitert.
       */
      rejoinableIds?.has(session.id) === true ? (
        <Button
          type="button"
          size={size}
          disabled={busyId === session.id}
          onClick={() => onRejoin?.(session.id)}
          title="Startet eine neue Session mit demselben Meeting und Avatar"
        >
          {busyId === session.id ? 'startet …' : 'Erneut beitreten'}
        </Button>
      ) : null
    ) : (
      <>
        <Button
          type="button"
          variant="danger"
          size={size}
          disabled={busyId === session.id}
          onClick={() => onStop?.(session.id)}
        >
          {busyId === session.id ? 'Stoppt …' : 'Stoppen'}
        </Button>
        {/*
          Derselbe Weg wie die Taste "m", nur ohne Tastatur — auf Handy und iPad
          gibt es keine. Der Knopf zeigt KEINEN Zustand an: ob das Mikrofon
          danach offen oder zu ist, weiss nur das Realtime-Werkzeug, und eine
          geratene Anzeige waere schlimmer als keine.
        */}
        <Button
          type="button"
          size={size}
          onClick={() => onMic?.(session.id)}
          title="Schaltet in goDialogue 2.0 das Mikrofon auf und zu (Taste m)"
        >
          {micSentId === session.id ? 'gesendet ✓' : 'Mikro'}
        </Button>
        <Button
          type="button"
          size={size}
          onClick={() => onScreenshot?.(session.id)}
          title="Aktuelles Bild holen — genau das, was im Meeting ankommt"
        >
          Bild
        </Button>
        <Button type="button" size={size} onClick={() => onToggleLog?.(session.id)}>
          {openLogId === session.id ? 'Zu' : 'Ton & Log'}
        </Button>
      </>
    );

  const zeitspalte = (session: SessionSummary): ReactNode =>
    variant === 'active' ? (
      <span className="tabular">{formatDuration(session.runtimeSeconds)}</span>
    ) : (
      <>
        <span className="block tabular">{formatTimestamp(session.endedAt)}</span>
        <span className="mt-0.5 block text-xs text-fg-muted">
          {session.endReason === null
            ? formatDuration(session.runtimeSeconds)
            : `${SESSION_END_REASON_LABELS[session.endReason]} · ${formatDuration(session.runtimeSeconds)}`}
        </span>
      </>
    );

  return (
    <>
      {/* --- Handy: eine Karte je Session ---------------------------------- */}
      <ul className="space-y-3 md:hidden">
        {sessions.map((session) => (
          <li key={session.id} className="rounded-xl border border-border bg-bg p-4">
            <div className="flex items-start justify-between gap-3">
              <StatusBadge state={session.state} />
              <span className="text-right text-sm text-fg-subtle">{zeitspalte(session)}</span>
            </div>
            <p className="mt-3 font-medium text-fg">{session.displayName}</p>
            <p className="mt-0.5 text-sm text-fg-subtle">{session.meetingLabel}</p>
            <p className="mt-0.5 text-xs text-fg-muted">gestartet von {session.startedBy}</p>
            {variant === 'active' && (
              <p className="mt-2 text-xs text-fg-muted">{SESSION_STATE_HINTS[session.state]}</p>
            )}
            {session.error !== null && (
              <p className="mt-2 rounded-lg bg-danger/10 px-2.5 py-1.5 text-xs text-danger">
                {session.error}
              </p>
            )}
            {actions(session, 'md') !== null && (
              <div className="mt-4 flex flex-wrap gap-2">{actions(session, 'md')}</div>
            )}
          </li>
        ))}
      </ul>

      {/* --- Ab Tablet: Tabelle -------------------------------------------- */}
      {/* overflow-x-auto statt -hidden: zwischen Tabletbreite und breitem
          Fenster passt die Tabelle nicht immer. Abschneiden waere schlimmer als
          scrollen — auf dem Handy uebernehmen ohnehin die Karten oben. */}
      <div className="hidden overflow-x-auto rounded-xl border border-border bg-bg md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-bg-elevated text-[11px] tracking-wide text-fg-muted uppercase">
            <tr>
              <th className="px-4 py-2.5 font-medium">Status</th>
              {/* Feste Mindestbreite: die Spaltenueberschrift ist kurz, der Inhalt
                  nicht. Ohne das schrumpft die Spalte auf die Breite von "AVA"
                  und der Name bricht buchstabenweise um. */}
              <th className="min-w-56 px-4 py-2.5 font-medium">AVA</th>
              <th className="px-4 py-2.5 font-medium">Meeting</th>
              <th className="px-4 py-2.5 font-medium">Gestartet von</th>
              <th className="px-4 py-2.5 font-medium">
                {variant === 'active' ? 'Laufzeit' : 'Ende'}
              </th>
              <th className="px-4 py-2.5 font-medium">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sessions.map((session) => (
              <tr key={session.id} className="align-top">
                <td className="px-4 py-3.5">
                  <StatusBadge state={session.state} />
                  {/*
                    Der Zustandshinweis nur bei laufenden Sessions: in der
                    Historie steht der Grund schon in der Ende-Spalte, und die
                    doppelte Angabe kostet so viel Breite, dass die Aktionsspalte
                    aus dem Bild laeuft.
                  */}
                  {variant === 'active' && (
                    <p className="mt-1.5 max-w-56 text-xs text-fg-muted">
                      {SESSION_STATE_HINTS[session.state]}
                    </p>
                  )}
                  {session.error !== null && (
                    <p className="mt-1.5 max-w-56 text-xs text-danger">{session.error}</p>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <p className="font-medium text-fg">{session.displayName}</p>
                  {/* Gekuerzt statt umgebrochen: die volle URL ist selten
                      interessant, aber sie zerreisst die Zeile. Ganz steht sie
                      im title. */}
                  <p
                    className="mt-0.5 max-w-56 truncate text-xs text-fg-muted"
                    title={session.avatarUrl}
                  >
                    {session.avatarUrl}
                  </p>
                </td>
                {/* Gekuerzt wie die AVA-Adresse: sonst draengt die Spalte die
                    Aktionen aus dem Bild. Ganz steht sie im title. */}
                <td className="px-4 py-3.5 text-fg-subtle">
                  <span className="block max-w-48 truncate" title={session.meetingLabel}>
                    {session.meetingLabel}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-fg-subtle">{session.startedBy}</td>
                <td className="px-4 py-3.5 text-fg-subtle">{zeitspalte(session)}</td>
                <td className="px-4 py-3.5">
                  <div className="flex flex-wrap gap-2">{actions(session, 'sm')}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
